/**
 * Die Umformung eines Lieferanten von der Drahtform des Servers (`{ kLieferant, jtl, extras }`) in den
 * flachen `Supplier` der Oberflaeche: `entschachtele` und `extrasFlach`.
 *
 * Bis zum KI-Zugang (Stufe 1) standen die beiden Funktionen in `api/lieferanten.ts`. Sie sind
 * UNVERAENDERT hierher verschoben, weil der Server (`backend/src/ki/auswertung/frontend/`) dieselbe
 * Umformung fahren muss wie die Oberflaeche und deshalb eine zeichengleiche Kopie dieses Moduls
 * braucht; `api/lieferanten.ts` fuehrt beide Namen als Re-Export weiter (`LieferantenSeite.tsx`
 * importiert `extrasFlach` weiter von dort).
 *
 * Regel fuer dieses Modul (`test/lib/reineKette.test.ts`): nur `.ts`-Importe innerhalb von
 * `frontend/src`, kein `react`, kein `import.meta`, keine Browser-Globals.
 */
import type { Supplier } from "./types.ts";

/**
 * Loest die Verschachtelung von `GET /api/suppliers/:kLieferant` auf: `{ kLieferant, jtl, extras }`
 * wird zu einem flachen `Supplier` - GEMESSEN gegen `ladeLieferant()`
 * (`backend/src/domaene/lieferanten.ts:132-145`), nicht angenommen.
 *
 * ZWEI verschachtelte Objekte, nicht eines wie bei den Bestellungen:
 *
 *   `jtl`    - live aus JTL (`backend/src/jtl/typen.ts#JtlLieferant`, :3-13), `null` ohne
 *              konfigurierten JTL-Zugang. Felder: kLieferant, firma, kontakt, email,
 *              lieferzeitTage, zahlungszielTage, skontoProzent, mindestbestellwert, aktiv.
 *   `extras` - die `supplier_extras`-ZEILE ueber `oeffentlich()` (`backend/migrations/
 *              005_domaene.sql:36-47`), `null` ohne eigene Zusatzfeldzeile. Felder: kLieferant,
 *              eur, shortName, paymentPlan, logo, notes, rowVersion, updatedAt, updatedBy.
 *              (`paymentTemplate` stand hier bis Migration 039 (Fixrunde 1, 2026-09-07) -
 *              `supplier_extras.payment_template` ist geloescht.)
 *
 * KOLLISIONSFRAGE (Auftrag Fix-Runde 2, Punkt 1): tragen `jtl` und `extras` gleichnamige
 * Schluessel? Einzige Ueberschneidung ist `kLieferant` - dieselbe JTL-Kennung liegt DREIFACH vor
 * (Kopf, `jtl.kLieferant`, `extras.kLieferant`), eine reine Wiederholung wie `orderId` bei den
 * Bestellungen. Beide Kopien fallen deshalb weg, der Kopf liefert die einzige `id`.
 *
 * Anders als bei den Bestellungen gibt es hier KEINE Kollision zwischen zwei INHALTLICH
 * verschiedenen Bedeutungen desselben Namens: `jtl` fuehrt kein `rowVersion`/`updatedAt`/
 * `updatedBy` - JTL-Stammdaten kommen live und werden nicht versioniert. Diese drei Namen stammen
 * deshalb ausschliesslich aus `extras` und brauchen (anders als dort) keinen eigenen Praefix wie
 * `extrasRowVersion`. Die Gegenprobe dazu steht als eigener Test in `lieferanten.test.ts`
 * (RIEGEL, Kollisionsteil): sie laeuft gegen die ECHTE Antwort von `ladeLieferant()`, nicht gegen
 * eine von Hand gepflegte Liste - eine kuenftige, wirklich kollidierende Spalte wuerde den Test
 * dort zu Fall bringen, nicht nur diesen Kommentar Luegen strafen.
 *
 * NAME-ALIAS: der Firmenname liegt in JTL unter `firma` (cFirma), nicht unter `name` -
 * `lib/suppliers.ts` (`supplierByName` :216-222, `supplierById` ueber `s.id` :157-160,
 * `orderSupplier` :224-235) und `components/Drawer/MainTab/Stammdaten.tsx:46` lesen aber `s.name`
 * bzw. `s.id`. `firma` bleibt zusaetzlich stehen (der gemessene JTL-Feldname), `name` wird als
 * Alias ERGAENZT - sonst faende die Lieferantenaufloesung weiterhin nichts, obwohl der Datensatz
 * jetzt flach ist (Befund 2 im Kopfkommentar).
 *
 * Wie bei `bestellungen.ts#entschachtele`: Destrukturierung und Spread, NICHT
 * `ergebnis[feld] = wert` in einer Schleife - derselbe `__proto__`-Grund steht dort ausfuehrlich.
 */
/**
 * Die oeffentliche `supplier_extras`-Zeile in GENAU die flache Form, die diese Naht fuehrt: alles
 * ausser `kLieferant`.
 *
 * EIGENE, EXPORTIERTE FUNKTION SEIT DER FIX-RUNDE 1 ZU TASK 11 (2026-08-11), weil es seither ZWEI
 * Stellen gibt, an denen dieselbe Zeile ankommt:
 *
 *   LESEN     `GET /api/suppliers/:kLieferant` -> `entschachtele()` unten
 *   SCHREIBEN `PATCH /api/suppliers/:kLieferant/extras` antwortet mit derselben Zeile ueber
 *             `oeffentlich()` (`backend/src/http/routes/suppliers.ts:83`) ->
 *             `Suppliers/LieferantenSeite.tsx#uebernimmGespeicherteZeile`
 *
 * Der Schreibweg legte die Antwort bis zur Fix-Runde 1 ROH ueber den gelesenen Datensatz und trug
 * `kLieferant` damit zurueck, das `entschachtele()` bewusst entfernt hatte - derselbe Datensatz sah
 * nach dem Speichern anders aus als nach dem Lesen. Eine Regel, zwei Aufrufer: so koennen die beiden
 * Formen nicht mehr auseinanderlaufen.
 */
export function extrasFlach(zeile: Record<string, unknown>): Record<string, unknown> {
  const { kLieferant: _kennung, ...rest } = zeile;
  return rest;
}

export function entschachtele(datensatz: unknown): Supplier {
  if (typeof datensatz !== "object" || datensatz === null || Array.isArray(datensatz)) {
    return datensatz as Supplier;
  }
  const { kLieferant, jtl, extras } = datensatz as Record<string, unknown>;

  let jtlFelder: Record<string, unknown> = {};
  if (typeof jtl === "object" && jtl !== null && !Array.isArray(jtl)) {
    const { kLieferant: _jtlKLieferant, firma, ...rest } = jtl as Record<string, unknown>;
    jtlFelder = { ...rest, firma, name: firma };
  }

  let extrasFelder: Record<string, unknown> = {};
  if (typeof extras === "object" && extras !== null && !Array.isArray(extras)) {
    extrasFelder = extrasFlach(extras as Record<string, unknown>);
  }

  return {
    id: kLieferant === undefined ? undefined : String(kLieferant),
    ...jtlFelder,
    ...extrasFelder,
  } as Supplier;
}
